import * as React from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useAppDispatch, useAppSelector } from '../../../State/Store';
import { fetchSellerOrders, updateOrderStatus } from '../../../State/seller/sellerOrderSlice';
import { Button, Menu, MenuItem, FormControl, InputLabel, Select, Box, SelectChangeEvent, Chip } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const orderStatus = [
  { color: '#FFA500', label: 'PENDING' },
  { color: '#F5BCBA', label: 'CONFIRMED' },
  { color: '#F5BCBA', label: 'PLACED' },
  { color: '#1E90FF', label: 'SHIPPED' },
  { color: '#32CD32', label: 'DELIVERED' },
  { color: '#FF0000', label: 'CANCELLED' },
] as const;

export default function OrderTable() {
  const dispatch = useAppDispatch();
  const { sellerOrder } = useAppSelector((store) => store);

  // Time filter state: 'ALL' or '24HRS'
  const [dateFilter, setDateFilter] = React.useState<string>('ALL');

  React.useEffect(() => {
    dispatch(fetchSellerOrders(localStorage.getItem('jwt') || ''));
  }, [dispatch]);

  // Status Menu Anchor State
  const [anchorElStatus, setAnchorElStatus] = React.useState<Record<number, HTMLElement | null>>({});

  // Print Menu Anchor State
  const [anchorElPrint, setAnchorElPrint] = React.useState<Record<number, HTMLElement | null>>({});

  const handleStatusClick = (event: React.MouseEvent<HTMLButtonElement>, orderId: number) => {
    setAnchorElStatus((prev) => ({ ...prev, [orderId]: event.currentTarget }));
  };

  const handleStatusClose = (orderId: number) => () => {
    setAnchorElStatus((prev) => ({ ...prev, [orderId]: null }));
  };

  const handlePrintClick = (event: React.MouseEvent<HTMLButtonElement>, orderId: number) => {
    setAnchorElPrint((prev) => ({ ...prev, [orderId]: event.currentTarget }));
  };

  const handlePrintClose = (orderId: number) => () => {
    setAnchorElPrint((prev) => ({ ...prev, [orderId]: null }));
  };

  const handleUpdateOrderStatus = (orderId: number, statusLabel: any) => () => {
    dispatch(updateOrderStatus({ jwt: localStorage.getItem('jwt') || '', orderId, orderStatus: statusLabel }));
    handleStatusClose(orderId)();
  };

  const handleFilterChange = (event: SelectChangeEvent) => {
    setDateFilter(event.target.value as string);
  };

  // Helper function to build a clean printable HTML invoice string
  const createInvoiceHTML = (order: any) => {
    const totalAmount = order.orderItems?.reduce(
      (sum: number, item: any) => sum + (item.product?.sellingPrice || 0) * (item.quantity || 1),
      0
    ) || order.totalSellingPrice || 0;

    const orderDateFormatted = order.orderDate
      ? new Date(order.orderDate).toLocaleString()
      : new Date().toLocaleDateString();

    const paymentMethod = order.paymentDetails?.paymentMethod || order.paymentMethod;
    const paymentStatus = order.paymentDetails?.paymentStatus || order.paymentStatus;
    const mpesaRef = order.paymentDetails?.mpesaReceiptNumber || order.mpesaReceiptNumber;

    return `
      <div id="receipt-content" style="padding: 24px; font-family: Arial, sans-serif; width: 600px; background: #fff; color: #333;">
        <div style="display: flex; justify-content: space-between; border-bottom: 2px solid #333; padding-bottom: 12px; margin-bottom: 20px;">
          <div>
            <h1 style="margin: 0; font-size: 22px;">ORDER RECEIPT</h1>
            <p style="margin: 4px 0 0 0; color: #666; font-size: 12px;">Order ID: #${order.id}</p>
          </div>
          <div style="text-align: right;">
            <p style="margin: 0; font-size: 12px; color: #666;">Date: ${orderDateFormatted}</p>
            <p style="margin: 4px 0 0 0; font-size: 12px; font-weight: bold;">Order Status: ${order.orderStatus}</p>
          </div>
        </div>

        <!-- Payment Details Section -->
        <div style="margin-bottom: 20px; padding: 12px; background: #f9f9f9; border-radius: 6px; border: 1px solid #eee;">
          <h3 style="margin: 0 0 8px 0; font-size: 14px; text-transform: uppercase; color: #555;">Payment Information</h3>
          <p style="margin: 2px 0; font-size: 13px;"><strong>Method:</strong> ${paymentMethod ? paymentMethod.replace(/_/g, ' ') : 'N/A'}</p>
          <p style="margin: 2px 0; font-size: 13px;"><strong>Status:</strong> ${paymentStatus || 'N/A'}</p>
          ${mpesaRef ? `<p style="margin: 2px 0; font-size: 13px;"><strong>M-Pesa Ref:</strong> ${mpesaRef}</p>` : ''}
        </div>

        <div style="margin-bottom: 20px;">
          <h3 style="margin: 0 0 8px 0; font-size: 14px; text-transform: uppercase; color: #555;">Shipping Address</h3>
          <p style="margin: 2px 0; font-size: 13px;"><strong>${order.shippingAddress?.name || ''}</strong></p>
          <p style="margin: 2px 0; font-size: 13px;">${order.shippingAddress?.address || ''}, ${order.shippingAddress?.city || ''}</p>
          <p style="margin: 2px 0; font-size: 13px;">${order.shippingAddress?.state || ''} - ${order.shippingAddress?.pinCode || ''}</p>
          <p style="margin: 2px 0; font-size: 13px;"><strong>Mobile:</strong> ${order.shippingAddress?.mobile || ''}</p>
        </div>

        <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
          <thead>
            <tr style="background-color: #f2f2f2; text-align: left;">
              <th style="padding: 8px; border: 1px solid #ddd; font-size: 12px;">Item</th>
              <th style="padding: 8px; border: 1px solid #ddd; font-size: 12px;">Color</th>
              <th style="padding: 8px; border: 1px solid #ddd; font-size: 12px; text-align: right;">Price</th>
            </tr>
          </thead>
          <tbody>
            ${order.orderItems
              ?.map(
                (item: any) => `
              <tr>
                <td style="padding: 8px; border: 1px solid #ddd; font-size: 12px;">${item.product?.title || 'Product'}</td>
                <td style="padding: 8px; border: 1px solid #ddd; font-size: 12px;">${item.product?.color || 'N/A'}</td>
                <td style="padding: 8px; border: 1px solid #ddd; font-size: 12px; text-align: right;">KES ${item.product?.sellingPrice?.toLocaleString() || 0}</td>
              </tr>
            `
              )
              .join('')}
          </tbody>
        </table>

        <div style="text-align: right; border-top: 2px solid #333; padding-top: 10px;">
          <h3 style="margin: 0; font-size: 16px;">Total: KES ${totalAmount.toLocaleString()}</h3>
        </div>
      </div>
    `;
  };

  const handleDirectPrint = (order: any) => () => {
    handlePrintClose(order.id)();
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Receipt - Order #${order.id}</title>
          <style>
            body { margin: 0; padding: 20px; display: flex; justify-content: center; }
          </style>
        </head>
        <body>
          ${createInvoiceHTML(order)}
          <script>
            window.onload = function() {
              window.print();
              window.close();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleDownloadPDF = (order: any) => async () => {
    handlePrintClose(order.id)();

    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.innerHTML = createInvoiceHTML(order);
    document.body.appendChild(container);

    try {
      const element = container.querySelector('#receipt-content') as HTMLElement;
      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);
      pdf.save(`Order_Receipt_${order.id}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      document.body.removeChild(container);
    }
  };

  // 1. Filter logic: Filter by Last 24 Hours if selected
  const now = new Date().getTime();
  const twentyFourHoursMs = 24 * 60 * 60 * 1000;

  const filteredOrders = (sellerOrder.orders || []).filter((order: any) => {
    if (dateFilter === '24HRS') {
      if (!order.orderDate) return false;
      const orderTime = new Date(order.orderDate).getTime();
      return now - orderTime <= twentyFourHoursMs;
    }
    return true;
  });

  // 2. Sort logic: Latest orders first (descending order by orderDate or ID)
  const sortedOrders = [...filteredOrders].sort((a: any, b: any) => {
    const dateA = a.orderDate ? new Date(a.orderDate).getTime() : a.id;
    const dateB = b.orderDate ? new Date(b.orderDate).getTime() : b.id;
    return dateB - dateA;
  });

  return (
    <Box>
      {/* Top Controls: Filter by Date */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="date-filter-label">Date Filter</InputLabel>
          <Select
            labelId="date-filter-label"
            value={dateFilter}
            label="Date Filter"
            onChange={handleFilterChange}
          >
            <MenuItem value="ALL">All Orders</MenuItem>
            <MenuItem value="24HRS">Last 24 Hours</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 750 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell>Order Id</StyledTableCell>
              <StyledTableCell>Products</StyledTableCell>
              <StyledTableCell align="right">Shipping Address</StyledTableCell>
              <StyledTableCell align="center">Payment Details</StyledTableCell>
              <StyledTableCell align="right">Order Status</StyledTableCell>
              <StyledTableCell align="right">Update Status</StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedOrders.map((item: any) => {
              // Extract payment fields safely with fallback checks
              const rawPaymentMethod = item.paymentDetails?.paymentMethod || item.paymentMethod;
              const formattedPaymentMethod = rawPaymentMethod
                ? rawPaymentMethod.replace(/_/g, ' ')
                : 'N/A';

              const paymentStatus = item.paymentDetails?.paymentStatus || item.paymentStatus || 'PENDING';

              return (
                <StyledTableRow key={item.id}>
                  <StyledTableCell component="th" scope="row">
                    #{item.id}
                    {item.orderDate && (
                      <div className="text-[10px] text-gray-500 mt-1">
                        {new Date(item.orderDate).toLocaleString()}
                      </div>
                    )}
                  </StyledTableCell>

                  <StyledTableCell>
                    <div className="flex gap-2 flex-wrap">
                      {item.orderItems?.map((orderItem: any, index: number) => (
                        <div key={index} className="flex gap-3 items-center">
                          <img
                            className="w-16 h-16 object-cover rounded-md"
                            src={orderItem.product?.images?.[0]}
                            alt={orderItem.product?.title}
                          />
                          <div className="flex flex-col text-xs">
                            <span className="font-semibold">{orderItem.product?.title}</span>
                            <span>Price: KES {orderItem.product?.sellingPrice?.toLocaleString()}</span>
                            <span>Color: {orderItem.product?.color}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </StyledTableCell>

                  <StyledTableCell align="right">
                    <div className="flex flex-col gap-y-1 text-xs">
                      <span className="font-semibold">{item.shippingAddress?.name}</span>
                      <span>
                        {item.shippingAddress?.address}, {item.shippingAddress?.city}
                      </span>
                      <span>
                        {item.shippingAddress?.state}, {item.shippingAddress?.pinCode}
                      </span>
                      <span>
                        <strong>Mobile:</strong> {item.shippingAddress?.mobile}
                      </span>
                    </div>
                  </StyledTableCell>

                  {/* Payment Method & Payment Status Column */}
                  <StyledTableCell align="center">
                    <div className="flex flex-col gap-1 items-center text-xs">
                      <span className="font-semibold uppercase">{formattedPaymentMethod}</span>
                      <Chip
                        label={paymentStatus}
                        size="small"
                        color={
                          paymentStatus === 'SUCCESS' || paymentStatus === 'COMPLETED'
                            ? 'success'
                            : paymentStatus === 'FAILED'
                            ? 'error'
                            : 'warning'
                        }
                        variant="outlined"
                      />
                    </div>
                  </StyledTableCell>

                  <StyledTableCell align="right">
                    <span
                      className="px-3 py-1 border rounded-full text-xs font-semibold"
                      style={{ color: orderStatus.find((s) => s.label === item.orderStatus)?.color || '#333' }}
                    >
                      {item.orderStatus}
                    </span>
                  </StyledTableCell>

                  {/* Status Update Column */}
                  <StyledTableCell align="right">
                    <Button variant="outlined" size="small" onClick={(e) => handleStatusClick(e, item.id)}>
                      Status
                    </Button>
                    <Menu
                      id={`status-menu-${item.id}`}
                      anchorEl={anchorElStatus[item.id]}
                      open={Boolean(anchorElStatus[item.id])}
                      onClose={handleStatusClose(item.id)}
                    >
                      {orderStatus.map((status) => (
                        <MenuItem
                          key={status.label}
                          onClick={handleUpdateOrderStatus(item.id, status.label)}
                        >
                          {status.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </StyledTableCell>

                  {/* Print / Download Receipt Column */}
                  <StyledTableCell align="center">
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<PrintIcon />}
                      onClick={(e) => handlePrintClick(e, item.id)}
                    >
                      Receipt
                    </Button>
                    <Menu
                      id={`print-menu-${item.id}`}
                      anchorEl={anchorElPrint[item.id]}
                      open={Boolean(anchorElPrint[item.id])}
                      onClose={handlePrintClose(item.id)}
                    >
                      <MenuItem onClick={handleDirectPrint(item)}>
                        <PrintIcon fontSize="small" style={{ marginRight: 8 }} /> Print Receipt
                      </MenuItem>
                      <MenuItem onClick={handleDownloadPDF(item)}>
                        <PictureAsPdfIcon fontSize="small" style={{ marginRight: 8 }} /> Download PDF
                      </MenuItem>
                    </Menu>
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}